'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

const TIMELINE_KEYS = [
  'item1',
  'item2',
  'item3',
  'item4',
  'item5',
  'item6',
  'item7',
  'item8',
]

export const TheMartianAchievements = () => {
  const t = useTranslations('the_martian_hardcode.achievements')

  return (
    <section className='grid overflow-hidden bg-neutral-800 lg:grid-cols-[798fr_1122fr] lg:gap-5'>
      <div className='relative aspect-[375/420] lg:aspect-auto lg:min-h-[800px]'>
        <picture>
          <source
            media='(min-width: 1024px)'
            srcSet={ImageHelper.getImageUrlCdn(
              '/image/2026/10/19/tm_achievement.jpg'
            )}
          />
          <Image
            src={ImageHelper.getImageUrlCdn(
              '/image/2026/10/19/tm_achievement_m.jpg'
            )}
            alt={t('image_alt')}
            fill
            sizes='(min-width: 1024px) 42vw, 100vw'
            className='object-cover object-[50%_40%] lg:object-[50%_42%]'
          />
        </picture>
      </div>

      <div className='grid min-w-0 content-center gap-6 px-5 pb-10 pt-6 lg:gap-15 lg:px-0 lg:py-20'>
        <div className='grid gap-3 lg:gap-6 lg:pr-15'>
          <h2 className='font-criteria text-[28px] font-bold uppercase italic leading-[1.25] text-white lg:text-[32px] 2xl:text-[44px]'>
            {t.rich('title', {
              hl: (chunks) => (
                <span className='text-[#F56A24] max-lg:block'>{chunks}</span>
              ),
            })}
          </h2>

          <div className='grid gap-2 lg:flex lg:gap-5 lg:px-6'>
            {(['stat1', 'stat2'] as const).map((key) => (
              <div key={key} className='grid gap-1 lg:w-[260px] lg:gap-4'>
                <div className='flex items-end gap-2.5'>
                  <span className='font-criteria text-[44px] font-bold uppercase italic leading-[54px] text-white lg:text-[65px] lg:leading-[70px]'>
                    {t(`${key}.value`)}
                  </span>
                  <span className='text-base font-bold uppercase leading-6 text-white lg:text-lg lg:leading-[26px]'>
                    {t(`${key}.unit`)}
                    <span className='lg:hidden'> {t(`${key}.title`)}</span>
                  </span>
                </div>
                <div className='grid gap-1'>
                  <p className='text-lg font-bold uppercase leading-[26px] text-white max-lg:hidden'>
                    {t(`${key}.title`)}
                  </p>
                  <p className='text-sm text-neutral-400 lg:text-base lg:leading-6'>
                    {t(`${key}.description`)}
                  </p>
                </div>
              </div>
            ))}

            <div className='grid gap-1 pt-4 lg:flex-1 lg:gap-4 lg:pt-0'>
              <p className='font-criteria text-lg font-bold uppercase italic leading-6 text-[#F56A24] lg:flex lg:h-[70px] lg:items-end lg:text-2xl lg:leading-8'>
                {t('stat3.highlight')}
              </p>
              <div className='grid gap-1'>
                <p className='text-base font-bold uppercase leading-6 text-white lg:text-lg lg:leading-[26px]'>
                  {t('stat3.title')}
                </p>
                <p className='text-sm text-neutral-400 lg:text-base lg:leading-6'>
                  {t('stat3.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* min-w-0: grid item mặc định nở theo nội dung, làm carousel không có phần tràn để kéo */}
        <div className='grid min-w-0 gap-4 lg:gap-6'>
          <h3 className='font-criteria text-lg font-bold leading-[26px] tracking-[-0.02em] text-white lg:text-2xl lg:font-semibold lg:leading-[35px]'>
            {t('timeline_title')}
          </h3>

          {/* Timeline tràn ra mép phải màn hình, vuốt ngang để xem tiếp */}
          <Carousel
            opts={{ align: 'start', dragFree: true }}
            className='-mr-5 min-w-0 lg:mr-0'
          >
            <CarouselContent className='ml-0'>
              {TIMELINE_KEYS.map((key, index) => {
                const isLast = index === TIMELINE_KEYS.length - 1
                return (
                  <CarouselItem
                    key={key}
                    // Mốc timeline không click được → bỏ cursor-pointer mặc định của CarouselItem
                    className='flex basis-[247px] cursor-default flex-col gap-4 pl-0 pr-3 lg:basis-[252px] lg:gap-6'
                  >
                    <div className='relative h-3'>
                      <span className='absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-[#F56A24]' />
                      <span className='absolute left-[112px] top-0 size-3 rounded-full bg-white lg:left-[114px]' />
                      {isLast ? (
                        <ChevronRight className='absolute -right-1 top-1/2 size-4 -translate-y-1/2 text-[#F56A24]' />
                      ) : null}
                    </div>
                    <div className='grid flex-1 content-start gap-3 rounded-lg bg-black px-2 pb-2.5 pt-3'>
                      <p className='font-criteria text-sm font-medium italic leading-none text-[#F56A24]'>
                        {t(`timeline.${key}.year`)}
                      </p>
                      <div className='grid gap-1'>
                        <p className='font-criteria text-sm font-medium italic text-white'>
                          {t.rich(`timeline.${key}.title`, {
                            br: () => <br />,
                          })}
                        </p>
                        <p className='text-xs leading-4 text-neutral-400'>
                          {t(`timeline.${key}.description`)}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                )
              })}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  )
}
